import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Checkbox,
  useColorModeValue,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, CloseIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';

interface ValidationCardsProps {
  validations: Validation[];
  checklistData: { [validationId: string]: { checked: boolean } };
  onChecklistUpdate: (checklistData: {
    [validationId: string]: { checked: boolean };
  }) => Promise<boolean>;
  applicant?: {
    email?: string;
    phone_home_prefix?: string;
    phone_home?: string;
    phone_work_prefix?: string;
    phone_work?: string;
  } | null;
}

const ValidationCards: React.FC<ValidationCardsProps> = ({
  validations,
  checklistData,
  onChecklistUpdate,
  applicant,
}) => {
  const formatApplicantPhone = () => {
    const homePrefix = applicant?.phone_home_prefix?.trim();
    const homePhone = applicant?.phone_home?.trim();
    const workPrefix = applicant?.phone_work_prefix?.trim();
    const workPhone = applicant?.phone_work?.trim();

    if (homePhone) {
      return [homePrefix, homePhone].filter(Boolean).join(' ');
    }

    if (workPhone) {
      return [workPrefix, workPhone].filter(Boolean).join(' ');
    }

    return '';
  };

  const getValidationMessageWithContact = (validation: Validation) => {
    const baseMessage = validation.validation_message;

    if (validation.validation_id !== '112') {
      return baseMessage;
    }

    const email = applicant?.email?.trim();
    const phone = formatApplicantPhone();
    const contactDetails = [
      email ? `Email: ${email}` : '',
      phone ? `Phone: ${phone}` : '',
    ].filter(Boolean);

    if (contactDetails.length === 0) {
      return baseMessage;
    }

    return `${baseMessage} (${contactDetails.join(' | ')})`;
  };

  const getInitialChecklistData = () => {
    if (Object.keys(checklistData).length > 0) {
      return checklistData;
    }

    const initialData: { [validationId: string]: { checked: boolean } } = {};
    validations.forEach((v) => {
      const isProblematic =
        v.validation_outcome === 'caution' ||
        v.validation_outcome === 'fail' ||
        v.validation_outcome === 'error';

      initialData[v.validation_id] = { checked: !isProblematic };
    });

    return initialData;
  };

  const [localChecklistData, setLocalChecklistData] = useState<{
    [validationId: string]: { checked: boolean };
  }>(getInitialChecklistData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setLocalChecklistData(getInitialChecklistData());
  }, [checklistData, validations]);

  const bgColor = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const cautionBgColor = useColorModeValue(
    'orange.50',
    'rgba(251, 146, 60, 0.1)',
  );
  const cautionBorderColor = useColorModeValue('orange.200', 'orange.300');
  const failBgColor = useColorModeValue('red.50', 'rgba(245, 101, 101, 0.1)');
  const failBorderColor = useColorModeValue('red.200', 'red.300');

  // Filter validations by outcome
  const cautionValidations = validations.filter(
    (v) => v.validation_outcome === 'caution',
  );
  const passValidations = validations.filter(
    (v) => v.validation_outcome === 'pass',
  );
  const failValidations = validations.filter(
    (v) => v.validation_outcome === 'fail',
  );
  const errorValidations = validations.filter(
    (v) => v.validation_outcome === 'error',
  );

  const handleCheckboxChange = (validationId: string, isChecked: boolean) => {
    setLocalChecklistData((prev) => ({
      ...prev,
      [validationId]: { checked: isChecked },
    }));
  };

  const handleSaveChanges = async () => {
    setIsSubmitting(true);

    try {
      const success = await onChecklistUpdate(localChecklistData);
      if (success) {
        toast({
          title: 'Checklist updated',
          description: 'Validation checklist has been saved successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      } else {
        throw new Error('Failed to update checklist');
      }
    } catch (error) {
      toast({
        title: 'Error updating checklist',
        description: 'Failed to save checklist changes. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      setLocalChecklistData(checklistData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges =
    JSON.stringify(localChecklistData) !== JSON.stringify(checklistData);

  const getValidationDisplay = (outcome: ValidationOutcome) => {
    switch (outcome) {
      case 'pass':
        return {
          icon: CheckCircleIcon,
          color: 'green.500',
          colorScheme: 'green',
          bgColor,
          borderColor,
        };
      case 'caution':
        return {
          icon: WarningIcon,
          color: 'orange.500',
          colorScheme: 'orange',
          bgColor: cautionBgColor,
          borderColor: cautionBorderColor,
        };
      case 'fail':
        return {
          icon: CloseIcon,
          color: 'red.500',
          colorScheme: 'red',
          bgColor: failBgColor,
          borderColor: failBorderColor,
        };
      case 'error':
        return {
          icon: WarningIcon,
          color: 'red.600',
          colorScheme: 'red',
          bgColor: failBgColor,
          borderColor: failBorderColor,
        };
      default:
        return {
          icon: CheckCircleIcon,
          color: 'gray.500',
          colorScheme: 'gray',
          bgColor,
          borderColor,
        };
    }
  };

  const renderValidationCard = (validation: Validation) => {
    const isChecked =
      localChecklistData[validation.validation_id]?.checked ?? true;
    const display = getValidationDisplay(validation.validation_outcome);
    const showCheckbox =
      validation.validation_outcome === 'caution' ||
      validation.validation_outcome === 'fail' ||
      validation.validation_outcome === 'error';

    return (
      <Card
        key={validation.validation_id}
        bg={display.bgColor}
        borderColor={display.borderColor}
        borderWidth="1px"
        p={4}
        mb={3}
      >
        <HStack align="start" spacing={4}>
          <Icon as={display.icon} color={display.color} boxSize={5} mt={1} />

          <VStack align="start" spacing={2} flex={1}>
            <HStack justify="space-between" width="100%">
              <VStack align="start" spacing={1} flex={1}>
                <HStack>
                  <Text fontWeight="semibold" fontSize="md">
                    {validation.validation_name}
                  </Text>
                  <Badge
                    colorScheme={display.colorScheme}
                    variant="subtle"
                    size="sm"
                  >
                    {validation.validation_outcome.toUpperCase()}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {validation.validation_type}
                </Text>
              </VStack>

              {showCheckbox && (
                <Checkbox
                  isChecked={isChecked}
                  onChange={(e) =>
                    handleCheckboxChange(
                      validation.validation_id,
                      e.target.checked,
                    )
                  }
                  size="lg"
                  colorScheme="green"
                ></Checkbox>
              )}
            </HStack>

            <Text fontSize="sm" color="gray.700">
              {validation.description}
            </Text>

            {validation.validation_message && (
              <Alert
                status={
                  validation.validation_outcome === 'error' ||
                  validation.validation_outcome === 'fail'
                    ? 'error'
                    : 'warning'
                }
                size="sm"
                borderRadius="md"
              >
                <AlertIcon boxSize={3} />
                <Text fontSize="sm">
                  {getValidationMessageWithContact(validation)}
                </Text>
              </Alert>
            )}
          </VStack>
        </HStack>
      </Card>
    );
  };

  if (validations.length === 0) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertTitle>No validations found</AlertTitle>
        <AlertDescription>
          No validation data is available for this policy.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary View Card */}
      <Card
        bg={bgColor}
        borderColor={borderColor}
        borderWidth="1px"
        p={6}
        mb={6}
      >
        <VStack spacing={4}>
          <Text fontSize="xl" fontWeight="bold" textAlign="center">
            Validation Summary
          </Text>

          <HStack spacing={12} wrap="wrap" justify="center">
            {/* Pass Count */}
            <VStack spacing={3} align="center" minW="120px">
              <HStack spacing={3}>
                <Icon as={CheckCircleIcon} color="green.500" boxSize={8} />
                <Text fontSize="4xl" fontWeight="bold" color="green.500">
                  {passValidations.length}
                </Text>
              </HStack>
              <Text
                fontSize="lg"
                fontWeight="semibold"
                color="gray.700"
                textAlign="center"
              >
                Passed
              </Text>
            </VStack>

            {/* Caution Count */}
            <VStack spacing={3} align="center" minW="120px">
              <HStack spacing={3}>
                <Icon as={WarningIcon} color="orange.500" boxSize={8} />
                <Text fontSize="4xl" fontWeight="bold" color="orange.500">
                  {cautionValidations.length}
                </Text>
              </HStack>
              <Text
                fontSize="lg"
                fontWeight="semibold"
                color="gray.700"
                textAlign="center"
              >
                Caution
              </Text>
            </VStack>

            {/* Failed Count */}
            <VStack spacing={3} align="center" minW="120px">
              <HStack spacing={3}>
                <Icon as={CloseIcon} color="red.500" boxSize={8} />
                <Text fontSize="4xl" fontWeight="bold" color="red.500">
                  {failValidations.length}
                </Text>
              </HStack>
              <Text
                fontSize="lg"
                fontWeight="semibold"
                color="gray.700"
                textAlign="center"
              >
                Failed
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </Card>

      {/* Error Validations */}
      {errorValidations.length > 0 && (
        <Box mb={6}>
          <Text fontSize="lg" fontWeight="semibold" mb={4} color="red.600">
            Errors ({errorValidations.length})
          </Text>
          {errorValidations.map((validation) =>
            renderValidationCard(validation),
          )}
        </Box>
      )}

      {/* Failed Validations */}
      {failValidations.length > 0 && (
        <Box mb={6}>
          {failValidations.map((validation) =>
            renderValidationCard(validation),
          )}
        </Box>
      )}

      {/* Caution Validations */}
      {cautionValidations.length > 0 && (
        <Box mb={6}>
          {cautionValidations.map((validation) =>
            renderValidationCard(validation),
          )}
        </Box>
      )}

      {/* Pass Validations */}
      {passValidations.length > 0 && (
        <Box mb={6}>
          {passValidations.map((validation) =>
            renderValidationCard(validation),
          )}
        </Box>
      )}

      {/* Save Button */}
      {hasChanges && (
        <Box position="sticky" bottom={4} zIndex={10}>
          <Card bg={bgColor} p={4} shadow="lg" borderWidth="1px">
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">
                You have unsaved changes to the validation checklist
              </Text>
              <Button
                colorScheme="brand"
                size="sm"
                onClick={handleSaveChanges}
                isLoading={isSubmitting}
                loadingText="Saving..."
              >
                Save Changes
              </Button>
            </HStack>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default ValidationCards;
